
import * as React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import type { RequestItem, RequestStatus } from 'features/dashboard/types/dashboard';

export type UseCoiRequestsResult = {
  requests: RequestItem[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  approveRequest: (id: string) => Promise<void>;
  declineRequest: (id: string) => Promise<void>;
};

type ApiMasterCoiRequest = {
  holderName: string;
  holderAddress: string;
  holderCity: string;
  holderState: string;
  holderZip: string;
  policyLOB: string;
  additionalInterestRequested: boolean;
  waverSubrogationrequested: boolean;
  additionalrequirements: string;
  createDate: string;
  changeDate: string;
  dueDate: string;
  databaseId: string;
  description: string;
  status: string;
  insuredId: string;
  insuredName: string;
  insuredEmail: string;
  insuredPhone: string;
  policyNumber: string;
  stage: string;
  customType: string;
  agents: any[];
  policies: any[];
};


const getAuthToken = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem('igtoken');
};

function mapStatus(apiStatus: string): RequestStatus {
  const normalized = apiStatus.toLowerCase();

  if (normalized === 'approved') {
    return 'Approved';
  }

  if (normalized === 'rejected' || normalized === 'declined') {
    return 'Declined';
  }

  return 'Pending';
}

function mapApiToRequestItem(api: ApiMasterCoiRequest): RequestItem {
  const createdDate = new Date(api.createDate);
  const createdDateLabel = Number.isNaN(createdDate.getTime())
    ? api.createDate
    : createdDate.toLocaleDateString(undefined, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });

  // Map status from API to RequestStatus
  let status: RequestStatus = 'Pending';
  const normalized = (api.status || '').toLowerCase();
  if (normalized === 'approved' || normalized === 'completed') status = 'Approved';
  else if (normalized === 'rejected' || normalized === 'declined') status = 'Declined';
  else if (normalized === 'pending' || normalized === 'new' || normalized === 'in progress') status = 'Pending';

  return {
    id: api.databaseId,
    organizationName: api.holderName,
    contactName: api.insuredName,
    contactEmail: api.insuredEmail,
    typeLabel: (api.policyLOB || '').replace(/\bWorker's Compensation\b/g, "Workers' Compensation"),
    createdDateLabel,
    note: api.description,
    status
  };
}

export type CoiRequestStatusType = 'pending' | 'completed';

export function useCoiRequests(
  enabled: boolean = true,
  insuredId?: string,
  status: CoiRequestStatusType = 'pending'
): UseCoiRequestsResult {
  const [requests, setRequests] = React.useState<RequestItem[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(enabled);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadToken, setReloadToken] = React.useState(0);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setError('You are not authenticated. Please sign in again.');
      setIsLoading(false);
      return;
    }

    let isCancelled = false;


    const fetchRequests = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const ncToken = window.localStorage.getItem('NCToken') || '';
        let apiPath = '/api/IGIGCOI/master-coi-requests';
        if (status === 'pending') {
          apiPath += '/new';
        } else if (status === 'completed') {
          apiPath += '/completed';
        }
        let url = `${process.env.REACT_APP_COIPortal_Url}${apiPath}?top=100`;
        if (insuredId) {
          url += `&insuredId=${encodeURIComponent(insuredId)}`;
        }
        const response = await axios.get<{ value: ApiMasterCoiRequest[] }>(
          url,
          {
            headers: {
              'NCToken': ncToken,
              'Authorization': 'Bearer ' + token,
              'Accept': 'application/json'
            }
          }
        );

        if (!isCancelled) {
          const mapped = (response.data.value || []).map(mapApiToRequestItem);
          setRequests(mapped);
          setIsLoading(false);
        }
      } catch (err: any) {
        if (!isCancelled) {
          if (axios.isAxiosError(err) && err.response && err.response.status === 401) {
            window.localStorage.clear();
            navigate('/');
            return;
          }
          setError('Failed to load COI requests.');
          setIsLoading(false);
        }
      }
    };

    void fetchRequests();

    return () => {
      isCancelled = true;
    };
  }, [enabled, reloadToken, insuredId, status]);

  const refresh = React.useCallback(() => {
    setReloadToken((v) => v + 1);
  }, []);

  const updateStatus = React.useCallback(
    async (id: string, isApproved: boolean) => {
      const token = getAuthToken();
      const ncToken = window.localStorage.getItem('NCToken') || '';
      if (!token) {
        setError('You are not authenticated. Please sign in again.');
        return;
      }

      try {
        await axios.post(
          `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/approve-coi-request`,
          {
            database_id: id,
            status: isApproved ? '1' : '0'
          },
          {
            headers: {
              'NCToken': ncToken,
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );

        // After successful status update, refresh the requests from the API
        setReloadToken((v) => v + 1);
      } catch (err: any) {
        if (axios.isAxiosError(err) && err.response && err.response.status === 401) {
          window.localStorage.clear();
          navigate('/');
          return;
        }
        setError('Failed to update request status.');
      }
    },
    []
  );

  const approveRequest = React.useCallback(
    async (id: string) => {
      await updateStatus(id, true);
    },
    [updateStatus]
  );

  const declineRequest = React.useCallback(
    async (id: string) => {
      await updateStatus(id, false);
    },
    [updateStatus]
  );

  return {
    requests,
    isLoading,
    error,
    refresh,
    approveRequest,
    declineRequest
  };
}
